/* Kashira cart drawer: coupon field. The only endpoint that truthfully
   validates a code before checkout is /cart/update.js { discount } — the
   returned cart lists the code with an `applicable` flag. /discount/CODE
   302s to a 200 for ANY string (it just sets the cookie), so it can never
   tell a typo from a real code; it stays only as a fallback for shops where
   the update route is unavailable, and then the message stays at "saved for
   checkout" rather than claiming validity.

   Three outcomes, and only the first two are ever asserted to the shopper:
   'success' (the cart came back and the code is applicable), 'error' (the
   update route explicitly rejected it), 'pending' (we could not check —
   cookie set, confirmation deferred to checkout, no tick and no red ring). */
if (!customElements.get('kashira-coupon')) {
  customElements.define(
    'kashira-coupon',
    class KashiraCoupon extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.message = this.querySelector('.kashira-coupon__message');
        this.fieldWrapper = this.querySelector('.field');
        if (!this.form || !this.input || !this.message) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        // Typing a new code clears the rejected state. Spec 17: re-validate on
        // input — the field must never stay red while the shopper is fixing it.
        this.input.addEventListener('input', () => this.resetState());
      }

      resetState() {
        this.classList.remove('kashira-coupon--invalid');
        if (window.KashiraFieldState) window.KashiraFieldState.clear(this.input);
        // The applied plate is the one message that survives typing — the field
        // it replaced is hidden by then, so nothing can reach this anyway.
        if (this.classList.contains('kashira-coupon--applied')) return;
        this.message.classList.remove('kashira-coupon__message--error');
        this.message.hidden = true;
      }

      async onSubmit(event) {
        event.preventDefault();
        const code = this.input.value.trim();
        if (!code) return;
        const button = this.form.querySelector('button');
        button.disabled = true;
        try {
          this.show(await this.applyCode(code));
        } catch {
          // applyCode is total, so this is a defensive branch only — and even
          // here an unexplained failure is not evidence the code is bad.
          this.show('pending');
        } finally {
          button.disabled = false;
        }
      }

      async applyCode(code) {
        try {
          // A hung request never rejects, so without this the await would sit
          // forever: no catch, no `finally`, and APPLY stays disabled with no
          // explanation. The abort lands in the catch below and resolves to
          // 'pending', which is the honest answer for "we could not check".
          const timeout = AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined;
          const res = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code }),
            signal: timeout,
          });
          if (res.ok) {
            const cart = await res.json();
            if (Array.isArray(cart.discount_codes)) {
              const entry = cart.discount_codes.find((d) => (d.code || '').toLowerCase() === code.toLowerCase());
              if (!entry || !entry.applicable) return 'error';
              // A cart-level code changes the totals immediately — let each
              // surface re-render itself rather than showing stale numbers
              // next to a "saved" tick.
              this.refreshTotals(cart, code);
              return 'success';
            }
          } else if (res.status === 422) {
            // The update route understood the request and rejected the code.
            return 'error';
          }
        } catch {
          /* fall through to the cookie fallback */
        }
        // Nothing above VERIFIED anything: the update route was unreachable, or
        // answered with a status/shape we cannot read (it 401s behind the CLI
        // dev proxy). Set the checkout cookie so a good code is not lost, then
        // say only what is true. Claiming 'success' here put a green tick on a
        // code that was never checked; claiming 'error' would tell a shopper
        // their perfectly good code is bad. Neither is honest — 'pending' is.
        try {
          await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        } catch {
          /* best effort; the pending copy promises a check at checkout, not an
             applied discount, so it holds either way */
        }
        return 'pending';
      }

      refreshTotals(cart, code) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kashira-coupon', cartData: cart });
        }
        const footer = document.getElementById('main-cart-footer');
        if (!footer?.dataset.id) return;
        fetch(`${window.location.pathname}?sections=${footer.dataset.id}`)
          .then((r) => r.json())
          .then((sections) => {
            const html = sections[footer.dataset.id];
            const fresh =
              html && new DOMParser().parseFromString(html, 'text/html').querySelector('#main-cart-footer .js-contents');
            const target = footer.querySelector('.js-contents');
            if (!fresh || !target) return;
            target.innerHTML = fresh.innerHTML;
            // The coupon lives inside `.js-contents`, so the re-render this
            // very code triggered replaces it. Carry the applied state on to
            // the fresh element, or the confirmation is wiped by the totals it
            // just produced.
            const next = footer.querySelector('kashira-coupon');
            if (next && next !== this && typeof next.restore === 'function') next.restore(code);
          })
          .catch(() => {});
      }

      /* Re-applies the confirmation after a section re-render swapped this
         element out. Upgrade of the fresh element is synchronous with the
         innerHTML write, so its refs are already resolved here. */
      restore(code) {
        if (!this.input) return;
        this.input.value = code;
        this.show('success');
      }

      show(kind) {
        // Unhide first: a live region that changes while display:none is not
        // announced.
        this.message.hidden = false;

        if (kind === 'error') {
          this.classList.remove('kashira-coupon--applied');
          this.message.classList.remove('kashira-inline-success', 'is-shown');
          this.message.classList.add('kashira-coupon__message--error');
          this.classList.add('kashira-coupon--invalid');
          this.form.hidden = false;
          // Spec 17: a rejected code is a FIELD error, not a toast. Same
          // contract as every other field — invalid class on the element that
          // draws the border, 300ms shake, the message paragraph the markup
          // already ships reused as the helper (so the copy lives in one
          // place), aria wiring, and focus back on the input to retype.
          if (window.KashiraFieldState) {
            window.KashiraFieldState.invalid(this.input, {
              target: this.fieldWrapper || this.form,
              node: this.message,
              message: this.message.dataset.error,
            });
          } else {
            this.message.textContent = this.message.dataset.error;
          }
          this.input.focus();
          this.input.select();
          return;
        }

        this.classList.remove('kashira-coupon--invalid');
        this.message.classList.remove('kashira-coupon__message--error');
        if (window.KashiraFieldState) window.KashiraFieldState.clear(this.input);

        if (kind === 'pending') {
          // Unverified: no tick and no success plate — the plate is the theme's
          // "this worked" idiom and must not appear for something unconfirmed.
          // The field stays put so the code can still be corrected. Copy falls
          // back to the "saved for checkout" wording where the markup has not
          // been given a `data-pending` yet; neither claims validity.
          this.classList.remove('kashira-coupon--applied');
          this.message.classList.remove('kashira-inline-success', 'is-shown');
          this.message.textContent = this.message.dataset.pending || this.message.dataset.success;
          this.form.hidden = false;
          return;
        }

        this.showApplied();
      }

      /* Spec 17 inline success: "content replaces chrome, 300ms crossfade. Same
         pattern for newsletter + coupon success." The field and APPLY give way
         to the tinted confirmation plate. The message paragraph doubles as the
         plate so the copy stays in the one live region the markup ships.
         Sizes on the <svg> are presentational attributes, not styling — they
         keep the tick sane if the plate's CSS has not loaded. */
      showApplied() {
        this.message.innerHTML =
          '<span class="kashira-inline-success__icon" aria-hidden="true">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" focusable="false">' +
          '<circle cx="12" cy="12" r="12"/><path d="M6.6 12.4l3.5 3.5 7.3-7.3"/>' +
          '</svg></span><span class="kashira-inline-success__text"></span>';
        this.message.querySelector('.kashira-inline-success__text').textContent = this.message.dataset.success;
        this.message.classList.add('kashira-inline-success');
        this.classList.add('kashira-coupon--applied');
        this.form.hidden = true;
        // Next frame, so the plate has a rendered opacity:0 to fade away from.
        requestAnimationFrame(() => this.message.classList.add('is-shown'));
      }
    }
  );
}

/* Empty cart drawer: "From your wishlist" 2-up (spec 04 empty state).
   Renders up to two saved products from the device-local wishlist store so an
   empty cart still offers a path back into the catalogue. No-op when the
   wishlist is empty. */
if (!customElements.get('kashira-cart-wishlist')) {
  const inrCompact = (paise) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
      paise / 100
    );

  customElements.define(
    'kashira-cart-wishlist',
    class extends HTMLElement {
      async connectedCallback() {
        const store = window.KashiraWishlistStore;
        const handles = (store ? store.list() : []).slice(0, 2);
        if (!handles.length) return;

        const products = (
          await Promise.all(
            handles.map((h) =>
              fetch(`/products/${h}.js`)
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            )
          )
        ).filter(Boolean);
        if (!products.length) return;

        const heading = this.dataset.heading || '';
        this.innerHTML = `
          <p class="kashira-cart-wishlist__heading">${heading}</p>
          <ul class="kashira-cart-wishlist__list list-unstyled" role="list">
            ${products
              .map(
                (p) => `
              <li class="kashira-cart-wishlist__item">
                <a href="${p.url}" class="kashira-cart-wishlist__media">
                  ${
                    p.featured_image
                      ? `<img src="${p.featured_image}${
                          p.featured_image.includes('?') ? '&' : '?'
                        }width=300" alt="" loading="lazy" width="150" height="200">`
                      : ''
                  }
                </a>
                <a href="${p.url}" class="kashira-cart-wishlist__title">${p.title}</a>
                <p class="kashira-cart-wishlist__price">${inrCompact(p.price)}</p>
              </li>`
              )
              .join('')}
          </ul>`;
        this.hidden = false;
      }
    }
  );
}
